import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { useAuthContext } from '../context/AuthContext';
import handleImageErrorWithPasomaruFallback from '../utils/imageUtils';
import '../styles/SalesSummaryPage.css';

const SalesRegisterSummaryPage = () => {
  const { user: userInfo } = useAuthContext();
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date');
  const [data, setData] = useState({});
  const [registers, setRegisters] = useState([]);
  const [selectedRegisterId, setSelectedRegisterId] = useState(null);

  useEffect(() => {
    if (!userInfo) return;

    if (userInfo.is_superuser) {
      apiRequest("inventory/registers/")
        .then((res) => {
          setRegisters(res);
        })
        .catch((err) => console.error("レジ一覧取得失敗", err));
    } else {
      setSelectedRegisterId(userInfo.register_id);
    }
  }, [userInfo]);

  useEffect(() => {
    if (!date) return;
    const fetchSummary = async () => {
      try {
        const res = await apiRequest(`inventory/note_sales_details_by_date/?date=${date}`);
        setData(res);
      } catch (err) {
        console.error("集計取得失敗", err);
      }
    };
    fetchSummary();
  }, [date]);

  if (!date) return <div>❌ 日付が指定されていません</div>;
  if (!userInfo) return <div>🔒 ユーザー情報がありません</div>;

  return (
    <div className="SalesNotePage-container">
      {userInfo.is_superuser && (
        <div className="SalesSummaryPage-filters">
          <label>レジID選択: </label>
          <select
            value={selectedRegisterId || ''}
            onChange={(e) => setSelectedRegisterId(Number(e.target.value) || null)}
          >
            <option value="">すべて</option>
            {registers.map((reg) => (
              <option key={reg.id} value={reg.id}>
                レジID: {reg.id}（{reg.name}）
              </option>
            ))}
          </select>
        </div>
      )}

      {Object.entries(data)
        .filter(([regId]) => !selectedRegisterId || Number(regId) === Number(selectedRegisterId))
        .map(([regId, saleTypes]) => (
          <div key={regId}>
            <h3>🧾 レジID: {regId} 販売ノート {date}</h3>
            <div className="SalesSummaryPage-grid">
              {/* 通常販売 */}
              <div className="SalesSummaryPage-column">
                <h4>通常販売</h4>
                {Object.entries(saleTypes.normal || {}).map(([category, payments]) => {
                  if (category === '未分類') {
                    const allItems = Object.values(payments).flat();
                    const nonBagItems = allItems.filter((item) => !item.product_name.includes('レジ袋'));
                    if (nonBagItems.length === 0) return null;
                  }

                  return (
                    <div key={category}>
                      <h5>{category}</h5>
                      {Object.entries(payments).map(([paymentMethod, items]) => (
                        <div key={paymentMethod}>
                          <h6>支払方法: {paymentMethod}</h6>
                          <ul className="SalesSummaryPage-list">
                            {items
                              .filter((item) => item.product_name !== 'レジ袋')
                              .map((item, idx) => (
                                <li key={idx} className="SalesSummaryPage-item">
                                  <img
                                    src={`/media/product_images/${item.sku}.jpg?`}
                                    alt="商品画像"
                                    onError={(e) => handleImageErrorWithPasomaruFallback(e, item.sku)}
                                  />
                                  <div className="SalesSummaryPage-text">
                                    <div className="SalesSummaryPage-name">({item.inventory_id}) {item.product_name}</div>
                                    <div className="SalesSummaryPage-price">¥{item.unit_price}</div>
                                  </div>
                                </li>
                              ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* 免税 - 1F */}
              <div className="SalesSummaryPage-column">
                <h4>免税（1Fカテゴリ）</h4>
                {Object.entries(saleTypes.tax_exempt || {}).map(([category, payments]) => {
                  if (!category.includes('1F')) return null;
                  return (
                    <div key={category}>
                      <h5>{category}</h5>
                      <ul className="SalesSummaryPage-list">
                        {Object.values(payments)
                          .flat()
                          .filter((item) => item.product_name !== 'レジ袋')
                          .map((item, idx) => (
                            <li key={idx} className="SalesSummaryPage-item">
                              <img
                                src={`/media/product_images/${item.sku}.jpg?`}
                                alt="商品画像"
                                onError={(e) => handleImageErrorWithPasomaruFallback(e, item.sku)}
                              />
                              <div className="SalesSummaryPage-text">
                                <div className="SalesSummaryPage-name">({item.inventory_id}) {item.product_name}</div>
                                <div className="SalesSummaryPage-price">¥{item.unit_price}</div>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {/* 免税 - 2F */}
              <div className="SalesSummaryPage-column">
                <h4>免税（2Fカテゴリ）</h4>
                {Object.entries(saleTypes.tax_exempt || {}).map(([category, payments]) => {
                  if (!category.includes('2F')) return null;
                  return (
                    <div key={category}>
                      <h5>{category}</h5>
                      <ul className="SalesSummaryPage-list">
                        {Object.values(payments)
                          .flat()
                          .filter((item) => item.product_name !== 'レジ袋')
                          .map((item, idx) => (
                            <li key={idx} className="SalesSummaryPage-item">
                              <img
                                src={`/media/product_images/${item.sku}.jpg?`}
                                alt="商品画像"
                                onError={(e) => handleImageErrorWithPasomaruFallback(e, item.sku)}
                              />
                              <div className="SalesSummaryPage-text">
                                <div className="SalesSummaryPage-name">({item.inventory_id}) {item.product_name}</div>
                                <div className="SalesSummaryPage-price">¥{item.unit_price}</div>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

export default SalesRegisterSummaryPage;
